"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  BriefcaseBusiness,
  CheckCircle2,
  ChevronRight,
  Eye,
  EyeOff,
  Lock,
  ShieldCheck,
  Users,
  Zap,
} from "lucide-react";
import { getSession, setAdminSession } from "@/lib/auth";
import { api } from "@/lib/api";

export default function AdminEntryPage() {
  const router = useRouter();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCode, setShowCode] = useState(false);

  useEffect(() => {
    if (getSession()?.role === "admin") router.push("/admin/jobs");
  }, [router]);

  async function handleEnter() {
    if (!code) {
      setError("Enter the administrator passcode");
      return;
    }
    setLoading(true);
    try {
      const result = await api.loginAdmin(code) as { token: string };
      setAdminSession(result.token);
      router.push("/admin/jobs");
      return;
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      const backendUnavailable =
        message.includes("Failed to fetch") ||
        message.includes("NetworkError") ||
        message.includes("Load failed");
      setError(
        backendUnavailable
          ? "Backend server is not reachable. Please check API URL and backend server."
          : "Invalid administrator passcode"
      );
      setCode("");
      setLoading(false);
    }
  }

  const capabilities = [
    { icon: BriefcaseBusiness, title: "Job operations", text: "Review requests, bids and assignments." },
    { icon: Users, title: "Contractor network", text: "Manage professional profiles and coverage." },
    { icon: Zap, title: "Live dispatch", text: "Monitor active work and service progress." },
  ];

  return (
    <main className="min-h-screen bg-slate-50 text-slate-950">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-24 max-w-7xl items-center justify-between px-6">
          <Link href="/" className="flex items-center gap-3">
            <img src="/lastest-logo-fixer.png" alt="FixerLocal" className="h-12 w-auto" />
            <span className="hidden border-l border-slate-200 pl-4 text-xs font-black uppercase tracking-[0.18em] text-emerald-700 sm:block">
              Admin
            </span>
          </Link>
          <Link href="/" className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-emerald-700">
            <ArrowLeft size={16} />
            Public site
          </Link>
        </div>
      </header>

      <section className="mx-auto grid min-h-[calc(100vh-6rem)] max-w-7xl items-center gap-16 px-6 py-16 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="max-w-xl">
          <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-emerald-700">
            <CheckCircle2 size={15} />
            Secure operations portal
          </div>
          <h1 className="text-5xl font-black leading-[1.05] tracking-tight sm:text-6xl">
            Manage every service request with confidence.
          </h1>
          <p className="mt-6 max-w-lg text-lg font-medium leading-8 text-slate-500">
            One focused workspace for FixerLocal jobs, contractors, assignments and service operations.
          </p>

          <div className="mt-10 grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
            {capabilities.map(({ icon: Icon, title, text }) => (
              <div key={title} className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                  <Icon size={20} />
                </div>
                <div>
                  <h2 className="font-black text-slate-900">{title}</h2>
                  <p className="mt-1 text-sm font-medium leading-6 text-slate-500">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-lg justify-self-end rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_24px_70px_rgba(15,23,42,0.08)] sm:p-10"
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
            <ShieldCheck size={28} />
          </div>
          <h2 className="mt-7 text-3xl font-black tracking-tight">Administrator login</h2>
          <p className="mt-2 font-medium leading-7 text-slate-500">
            Enter your secure passcode to open the control panel.
          </p>

          <div className="mt-8">
            <label className="mb-2 block text-sm font-bold text-slate-700">Admin passcode</label>
            <div className="relative">
              <Lock className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <input
                type={showCode ? "text" : "password"}
                value={code}
                onChange={(event) => {
                  setCode(event.target.value);
                  setError("");
                }}
                onKeyDown={(event) => event.key === "Enter" && handleEnter()}
                placeholder="Enter passcode"
                autoFocus
                className="h-16 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-14 pr-14 text-base font-bold outline-none transition-all focus:border-emerald-600 focus:bg-white focus:ring-4 focus:ring-emerald-100"
              />
              <button
                type="button"
                onClick={() => setShowCode((visible) => !visible)}
                className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-emerald-700"
                aria-label={showCode ? "Hide passcode" : "Show passcode"}
              >
                {showCode ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-4 flex items-center gap-3 rounded-2xl border border-red-100 bg-red-50 p-4 text-sm font-bold text-red-700"
              >
                <ShieldCheck size={17} />
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          <button
            onClick={handleEnter}
            disabled={loading}
            className="mt-6 flex h-16 w-full items-center justify-center gap-3 rounded-2xl bg-emerald-600 text-lg font-black text-white shadow-lg shadow-emerald-100 transition-all hover:bg-emerald-700 active:scale-[0.99] disabled:opacity-50"
          >
            {loading ? (
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            ) : (
              <>
                Continue to admin
                <ChevronRight size={21} />
              </>
            )}
          </button>

          <p className="mt-6 text-center text-xs font-semibold leading-5 text-slate-400">
            Authorized FixerLocal administrators only.
          </p>
        </motion.div>
      </section>
    </main>
  );
}
